import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { fetchGoodThings, postGoodThing, type GoodThingEntry } from '../lib/api';

/**
 * Consecutive-day streak of good-thing entries, counting back from today (or
 * yesterday, so a not-yet-written-today streak still shows as "alive"). Turns
 * a one-off box into a habit worth keeping.
 */
export function goodThingStreak(entries: GoodThingEntry[]): number {
  if (!entries.length) return 0;
  const days = new Set(
    entries.map((e) => {
      const d = new Date(e.createdAt);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    }),
  );
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  // Allow the streak to be "current" if the last entry was today OR yesterday.
  if (!days.has(cursor.getTime())) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(cursor.getTime())) return 0;
  }
  let streak = 0;
  while (days.has(cursor.getTime())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * Three Good Things — a private daily micro-journal (Tolcher et al. 2024).
 *
 * The entry text is peer-invisible and append-only: the reflection is the
 * mechanism, and editing yesterday's entry defeats it. Shows the last three
 * inline; the rest open with "View all".
 */
export function DailyGoodThing() {
  const [entries, setEntries] = useState<GoodThingEntry[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchGoodThings()
      .then(setEntries)
      .catch(() => setEntries([]));
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    const result = await postGoodThing(body);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setDraft('');
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2200);
    try {
      const fresh = await fetchGoodThings();
      setEntries(fresh);
    } catch {
      // Keep the previous list; the user's entry is saved.
    }
  }

  const remaining = 280 - draft.length;
  const recent = entries?.slice(0, 3) ?? [];
  const earlier = entries?.slice(3) ?? [];
  const streak = useMemo(() => goodThingStreak(entries ?? []), [entries]);

  return (
    <section>
      <div className="gt-head">
        <h2>Three good things</h2>
        {streak >= 2 && <span className="gt-streak">{streak}-day streak</span>}
      </div>
      <p className="muted">
        A small daily reflection — what was something small that went well? Two minutes is enough.
        Only you see these.
      </p>
      <form onSubmit={onSubmit} className="stack">
        <textarea
          value={draft}
          maxLength={280}
          rows={2}
          placeholder="What was something small that went well today?"
          aria-label="Today's good thing"
          onChange={(event) => setDraft(event.target.value)}
        />
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: '0.85rem' }}>
            {remaining} characters left
          </span>
          <button type="submit" className="secondary" disabled={busy || !draft.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
        {justSaved && <p className="muted">Saved. Come back tomorrow.</p>}
        {error && <p className="error">{error}</p>}
      </form>

      {recent.length > 0 && (
        <div className="stack" style={{ marginTop: '1rem' }}>
          {recent.map((e) => (
            <GoodThingRow key={e.id} entry={e} />
          ))}
          {earlier.length > 0 && !expanded && (
            <button type="button" className="link" onClick={() => setExpanded(true)}>
              View all ({entries?.length ?? 0})
            </button>
          )}
          {expanded && earlier.map((e) => <GoodThingRow key={e.id} entry={e} />)}
        </div>
      )}
    </section>
  );
}

function GoodThingRow({ entry }: { entry: GoodThingEntry }) {
  const date = new Date(entry.createdAt);
  const label = date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return (
    // A quiet ledger row, not a full .card — these render INSIDE the hero
    // card, where nesting surface+shadow gave shadow-on-shadow.
    <div className="gt-row">
      <p className="muted" style={{ margin: 0, fontSize: '0.8rem' }}>
        {label}
      </p>
      <p style={{ margin: '0.25rem 0 0' }}>{entry.body}</p>
    </div>
  );
}
